/* ==========================================================================
   Lalla — quick-add.js
   Product card quick add:
   - single-variant product: added directly (explicit click, quantity 1);
   - product with options: opens a picker loaded from Shopify
     (/products/<handle>?section_id=quick-add) that reuses product.js —
     the size is never chosen for the customer.
   ========================================================================== */

(() => {
  if (customElements.get('quick-add-modal')) return;
  const L = window.Lalla;
  let productAssets = null;

  function loadProductAssets(el) {
    if (customElements.get('product-info')) return Promise.resolve();
    if (!productAssets) {
      productAssets = new Promise((resolve, reject) => {
        if (!document.querySelector(`link[href="${el.dataset.productCss}"]`)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = el.dataset.productCss;
          document.head.appendChild(link);
        }
        const script = document.createElement('script');
        script.src = el.dataset.productJs;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return productAssets;
  }

  class QuickAddModal extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.modal = this.querySelector('modal-dialog');
      this.body = this.querySelector('[data-quick-add-body]');
      this.loadingHTML = this.body.innerHTML;

      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quick-add]');
        if (!button) return;
        event.preventDefault();
        if (button.dataset.variantId) this.addDirect(button);
        else this.open(button);
      });

      // After a successful add from the picker, close it without moving focus
      // (the cart drawer, if any, takes over).
      L.subscribe('cart:add', (detail) => {
        if (!detail || detail.sectionId !== 'quick-add' || !this.modal.isOpen) return;
        this.modal.opener = null;
        this.modal.close();
      });
    }

    async open(button) {
      this.body.innerHTML = this.loadingHTML;
      this.modal.open(button);
      const url = new URL(button.dataset.quickAdd, window.location.origin);
      url.searchParams.set('section_id', 'quick-add');

      try {
        const [html] = await Promise.all([
          fetch(url).then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          }),
          loadProductAssets(this),
        ]);
        const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quick-add-root]');
        if (!root) throw new Error('quick-add: empty response');
        this.body.replaceChildren(document.importNode(root, true));
        const info = this.body.querySelector('product-info');
        if (info) info.externalOpener = button;
        this.body.querySelector('#QuickAddProductTitle')?.focus();
      } catch (error) {
        const fallback = document.createElement('div');
        fallback.className = 'quick-add__error';
        fallback.innerHTML = `<p class="notice notice--error"></p><a class="button button--secondary" href="${button.dataset.quickAdd}"></a>`;
        fallback.querySelector('p').textContent = this.dataset.error;
        fallback.querySelector('a').textContent = button.getAttribute('aria-label') || '';
        this.body.replaceChildren(fallback);
      }
    }

    async addDirect(button) {
      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      try {
        const sections = typeof L.cartSections === 'function' ? L.cartSections() : [];
        const response = await fetch(`${L.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }], sections, sections_url: window.location.pathname }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) throw new Error(data.description || this.dataset.error);

        L.publish('cart:add', { item: data, sections: data.sections, sectionId: 'card', opener: button });
        const cart = await (await fetch(`${L.routes.cart}.js`, { headers: { Accept: 'application/json' } })).json();
        L.publish('cart:update', { cart, source: 'quick-add' });
        L.announce(this.dataset.added);

        if (!document.querySelector('cart-drawer')) {
          button.classList.add('is-added');
          const label = button.querySelector('[data-quick-add-label]');
          if (label) label.textContent = this.dataset.addedShort;
          setTimeout(() => {
            button.classList.remove('is-added');
            if (label) label.textContent = label.dataset.default || '';
          }, 2200);
        }
      } catch (error) {
        L.announce(error.message || this.dataset.error, { assertive: true });
      } finally {
        button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('quick-add-modal', QuickAddModal);
})();
